// store/modules/templates.js
import axios from 'axios';

export default {
    namespaced: true,
    state: {
        templates: {},
        fetchedTypes: [],
    },
    mutations: {
        // Replaces the full by-type slice (not a pure accumulate-merge): a template deleted or
        // renamed server-side must disappear/update here too, not linger forever because it was
        // once merged in from a stale localStorage cache (see ensureTypeLoaded).
        replaceTypeTemplates(state, { type, templates }) {
            const next = {};
            Object.values(state.templates).forEach((template) => {
                if (template.type !== type) next[template.id] = template;
            });
            templates.forEach((template) => { next[template.id] = template; });
            state.templates = next;
        },
        markTypeFetched(state, type) {
            if (!state.fetchedTypes.includes(type)) {
                state.fetchedTypes = [...state.fetchedTypes, type];
            }
        },
        upsertTemplate(state, template) {
            state.templates = { ...state.templates, [template.id]: template };
        },
        removeTemplate(state, id) {
            const templates = { ...state.templates };
            delete templates[id];
            state.templates = templates;
        },
    },
    getters: {
        getTemplates(state) {
            return state.templates;
        },
        getTemplatesList(state) {
            return Object.values(state.templates);
        },
        getTemplatesByType(state) {
            return (type) => Object.values(state.templates).filter((template) => template.type === type);
        },
        getTemplatesByFolder(state) {
            return (type, folderId) => Object.values(state.templates)
                .filter((template) => template.type === type && (template.folder_id || null) === (folderId || null))
                .sort((a, b) => a.position - b.position);
        },
        isTypeFetched(state) {
            return (type) => state.fetchedTypes.includes(type);
        },
    },
    actions: {
        async fetchTemplates({ commit }, type) {
            try {
                const { data } = await axios.get(`${process.env.VUE_APP_API_HOST}/templates`, {
                    params: { type },
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });

                commit('replaceTypeTemplates', { type, templates: data.templates || [] });
                commit('markTypeFetched', type);

                try {
                    localStorage.setItem(`templates_${type}`, JSON.stringify(data.templates || []));
                } catch (e) {
                    console.error('localStorage quota exceeded, templates cache not updated:', e);
                }
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        },

        // cache-first: paint the cached list for this type immediately, then refresh in background
        async ensureTypeLoaded({ dispatch, commit, getters }, type) {
            if (getters.isTypeFetched(type)) return;

            const cached = localStorage.getItem(`templates_${type}`);

            if (cached) {
                try {
                    commit('replaceTypeTemplates', { type, templates: JSON.parse(cached) });
                    commit('markTypeFetched', type);
                } catch (e) {
                    console.error('Invalid templates cache:', e);
                }
                dispatch('fetchTemplates', type);
            } else {
                await dispatch('fetchTemplates', type);
            }
        },
    },
};
